import React, { useState } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import BlogLayout from '@/components/admin/BlogLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Save } from 'lucide-react';

interface Tag {
  id?: number;
  name: string;
  slug: string;
}

interface TagFormProps {
  tag?: Tag;
  isEditing?: boolean;
}

const TagForm: React.FC<TagFormProps> = ({ tag, isEditing = false }) => {
  const { errors } = usePage().props as any;
  
  const [formData, setFormData] = useState({
    name: tag?.name || '',
    slug: tag?.slug || '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from name if creating new
    if (field === 'name' && !isEditing) {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    const url = isEditing && tag?.id 
      ? `/admin/blog/tags/${tag.id}` 
      : '/admin/blog/tags';
    
    const method = isEditing && tag?.id ? 'put' : 'post';

    router.visit(url, {
      method: method as 'post' | 'put' | 'patch',
      data: { blog_tag: formData },
      onFinish: () => setIsSubmitting(false),
    });
  };

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Blog', href: '/admin/blog/posts' },
    { label: 'Tags', href: '/admin/blog/tags' },
    { label: isEditing ? 'Edit Tag' : 'New Tag' },
  ];

  return (
    <BlogLayout breadcrumbs={breadcrumbs}>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link href="/admin/blog/tags">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditing ? 'Edit Tag' : 'New Tag'}
            </h1>
            <p className="text-muted-foreground">
              {isEditing ? 'Update your tag' : 'Create a new tag'}
            </p>
          </div>
        </div>

        <div className="max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Tag Details</CardTitle>
              <CardDescription>
                Enter the details for your tag
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Tag name"
                />
                {errors?.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => handleChange('slug', e.target.value)}
                  placeholder="tag-slug"
                />
                {errors?.slug && (
                  <p className="text-sm text-red-500">{errors.slug}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  URL-friendly identifier
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" asChild>
                  <Link href="/admin/blog/tags">Cancel</Link>
                </Button>
                <Button onClick={handleSubmit} disabled={isSubmitting}>
                  <Save className="mr-2 h-4 w-4" />
                  {isEditing ? 'Update Tag' : 'Create Tag'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </BlogLayout>
  );
};

export default TagForm;
